import {
    ChangeDetectorRef,
    DestroyRef,
    Directive,
    EmbeddedViewRef,
    inject,
    Input,
    OnInit,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { Resource } from './acl.model';
import { AclService } from './acl.service';


@Directive({
    selector: '[apResource]'
})
export class AclResourceDirective<C> implements OnInit {

    @Input()
    set apResource(name: Resource | undefined) {
        this.resource.next(name || null);
    }

    @Input()
    set apResourceNoAccess(noAccess: boolean) {
        this.displayIfNoAccess.next(noAccess);
    }

    private resource = new BehaviorSubject<Resource | null>(null);

    private displayIfNoAccess = new BehaviorSubject<boolean>(false);

    private embeddedViewRef?: EmbeddedViewRef<C>;

    private templateRef = inject(TemplateRef<C>);

    private viewContainer = inject(ViewContainerRef);

    private acl = inject(AclService);

    private cdr = inject(ChangeDetectorRef);

    private destroyRef = inject(DestroyRef);

    ngOnInit() {
        combineLatest([this.resource, this.displayIfNoAccess, this.acl.allowed])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([resource, displayIfNoAccess, _]) => {
                const hasAccess = this.acl.can(resource);
                const shouldRenderContent = displayIfNoAccess ? !hasAccess : hasAccess;
                if (this.embeddedViewRef && !shouldRenderContent) {
                    this.viewContainer.clear();
                    this.embeddedViewRef = undefined;
                } else if (shouldRenderContent && !this.embeddedViewRef) {
                    this.embeddedViewRef = this.viewContainer.createEmbeddedView(this.templateRef);
                    this.cdr.detectChanges();
                }
            });
    }
}


type Behavior = 'show-if-granted' | 'hide-if-granted' | 'always-show';

type Context = { granted: boolean };


@Directive({
    selector: '[apAcl]'
})
export class AclDirective implements OnInit {

    @Input() set apAcl(value: Resource[] | Resource | null | undefined) {
        if (!value) {
            value = [];
        }
        if (!Array.isArray(value)) {
            value = [value];
        }
        this.resources.next(value);
    }

    @Input()
    set apAclBehavior(value: Behavior) {
        this.behavior.next(value);
    }

    private resources = new BehaviorSubject<Resource[]>([]);

    private behavior = new BehaviorSubject<Behavior>('show-if-granted');

    private embeddedViewRef?: EmbeddedViewRef<Context>;

    private templateRef = inject(TemplateRef<Context>);

    private viewContainer = inject(ViewContainerRef);

    private acl = inject(AclService);

    private cdr = inject(ChangeDetectorRef);

    private destroyRef = inject(DestroyRef);

    ngOnInit() {
        combineLatest([this.resources, this.behavior, this.acl.allowed])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([resources, behavior, _]) => {
                const granted = resources.length == 0 || resources.some(r => this.acl.can(r));
                const shouldRenderContent =
                    behavior === 'always-show'
                    || (behavior === 'show-if-granted' && granted)
                    || (behavior === 'hide-if-granted' && !granted);

                if (this.embeddedViewRef && !shouldRenderContent) {
                    this.viewContainer.clear();
                    this.embeddedViewRef = undefined;
                } else if (shouldRenderContent && !this.embeddedViewRef) {
                    this.embeddedViewRef = this.viewContainer.createEmbeddedView(this.templateRef, { granted });
                }
                if (this.embeddedViewRef) {
                    this.embeddedViewRef.context.granted = granted;
                    this.embeddedViewRef.markForCheck();
                }
                this.cdr.markForCheck();
            });
    }
}
